/**
 * Right-hand column on desktop (bottom of the page on phones): what the guest
 * picked, the estimated total, the cancellation policy link and Submit.
 */
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoIcon } from "../../../../components/icons";
import { DURATION, EASE_OUT } from "../../../../components/motion";
import { cn } from "../../../../utils/classnames";
import { FIELD_TYPE_DATE, GROUP_AIRPORT, SCHEDULE_FIELD_TYPES, SERVICE_CONFIG, findLocation } from "../constants";
import { formatDisplayDate } from "../dates";
import { AnimatedYen, Fold, SwapText, usePressMotion } from "./animated";
import { Divider } from "./layout";

const EMPTY_VALUE = "-";

const useLanguage = () => {
  const { i18n } = useTranslation();
  return i18n.language === "en" ? "en" : "jp";
};

/** The text shown for one summary row, or "-" when not filled in yet. */
const useSummaryValue = () => {
  const { t } = useTranslation();
  const language = useLanguage();

  return (fieldName, form, group) => {
    const location = form.locations[group];
    if (fieldName === "locationName") {
      const selectedLocation = findLocation(group, location.placeId);
      return selectedLocation ? t(`meetAssistInquiry.${selectedLocation.labelKey}`) : EMPTY_VALUE;
    }
    if (fieldName === "terminalName") {
      return location.terminal ? t(`meetAssistInquiry.Terminals.${location.terminal}`) : EMPTY_VALUE;
    }
    const value = form[fieldName];
    if (!value) return EMPTY_VALUE;
    if (SCHEDULE_FIELD_TYPES[fieldName] === FIELD_TYPE_DATE) return formatDisplayDate(value, language);
    return String(value);
  };
};

/** "2 (2 y.o, 5 y.o)" -- children with their ages, skipping ages not chosen yet. */
const useChildrenSummary = () => {
  const { t } = useTranslation();
  return (children, childAges) => {
    const chosenAges = childAges.filter((age) => age !== "");
    if (children === 0 || chosenAges.length === 0) return String(children);
    const ageList = chosenAges.map((age) => t("meetAssistInquiry.Passengers.AgeOption", { age })).join(", ");
    return `${children} (${ageList})`;
  };
};

const getRowLabelKey = (fieldName, group) =>
  fieldName === "locationName"
    ? `meetAssistInquiry.Summary.Rows.locationName.${group === GROUP_AIRPORT ? "airport" : "station"}`
    : `meetAssistInquiry.Summary.Rows.${fieldName}`;

export const SummaryCard = ({ serviceKey, selectedTier, form }) => {
  const { t } = useTranslation();
  const getSummaryValue = useSummaryValue();
  const getChildrenSummary = useChildrenSummary();
  const shouldReduceMotion = useReducedMotion();
  const { group, journey, summaryFields } = SERVICE_CONFIG[serviceKey];

  const rows = [
    [t("meetAssistInquiry.Summary.Rows.adults"), String(form.adults)],
    [t("meetAssistInquiry.Summary.Rows.children"), getChildrenSummary(form.children, form.childAges)],
    ...summaryFields.map((fieldName) => [t(getRowLabelKey(fieldName, group)), getSummaryValue(fieldName, form, group)]),
  ];

  return (
    <div className="tw:bg-white tw:border tw:border-solid tw:border-[#E8E8E8] tw:!rounded-[5px] tw:overflow-hidden">
      <div className="tw:h-[50px] tw:bg-[#282828] tw:flex tw:items-center tw:justify-center tw:font-hanken-grotesk tw:font-bold tw:text-[15px] tw:leading-[22px] tw:text-white">
        {t("meetAssistInquiry.Summary.Title")}
      </div>
      <div className="tw:px-5 tw:pt-6 tw:pb-5 tw:flex tw:flex-col tw:gap-y-6">
        <div className="tw:flex tw:flex-col tw:gap-y-2">
          <span className="tw:font-hanken-grotesk tw:font-semibold tw:text-[8px] tw:leading-[12px] tw:tracking-[3px] tw:uppercase tw:text-[#282828]">
            <SwapText value={t(`meetAssistInquiry.Journeys.${journey}`)} />
          </span>
          <span className="tw:font-libre-caslon-display tw:text-[22px] tw:leading-[26px] tw:text-[#212529]">
            <SwapText value={t(`meetAssistInquiry.Tiers.${selectedTier}`)} />
          </span>
        </div>
        <Divider />
        {/* A different service has different rows, so the list cross-fades;
            within one service each value slides in as the guest fills it. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.dl
            key={serviceKey}
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -8 }}
            transition={{ duration: DURATION.fast, ease: EASE_OUT }}
            className="tw:!mb-0 tw:grid tw:grid-cols-[130px_1fr] tw:gap-x-4 tw:gap-y-[6px] tw:font-hanken-grotesk tw:text-[13px] tw:leading-[18.5px]"
          >
            {rows.map(([label, value]) => (
              <div key={label} className="tw:contents">
                <dt className="tw:!font-normal tw:text-[#7C7B7B]">{label}</dt>
                <dd className="tw:!mb-0 tw:text-[#282828] tw:break-words tw:min-w-0">
                  <SwapText value={value} />
                </dd>
              </div>
            ))}
          </motion.dl>
        </AnimatePresence>
      </div>
    </div>
  );
};

/** One "label ... amount" line. */
const PriceLine = ({ label, amount, className }) => (
  <div className={cn("tw:flex tw:items-center tw:justify-between tw:gap-x-3", className)}>
    <span>{label}</span>
    <span className="tw:text-right tw:whitespace-nowrap">{amount}</span>
  </div>
);

export const TotalCard = ({ serviceKey, selectedTier, price }) => {
  const { t } = useTranslation();
  const { journey } = SERVICE_CONFIG[serviceKey];

  return (
    <div className="tw:bg-white tw:border tw:border-solid tw:border-[#E8E8E8] tw:!rounded-[5px] tw:overflow-hidden tw:font-hanken-grotesk">
      <div className="tw:px-5 tw:pt-[18px] tw:pb-[18px] tw:flex tw:flex-col tw:gap-y-[2px] tw:text-[#4E4E4E]">
        <span className="tw:font-bold tw:text-[14px] tw:leading-[20px]">{t("meetAssistInquiry.Total.Heading")}</span>
        <span className="tw:text-[12px] tw:leading-[17px]">
          <SwapText value={`${t(`meetAssistInquiry.Journeys.${journey}`)} • ${t(`meetAssistInquiry.Tiers.Short.${selectedTier}`)}`} />
        </span>
      </div>

      <div className="tw:bg-[#F9F9F9] tw:p-5 tw:flex tw:flex-col tw:gap-y-[14px]">
        <PriceLine
          label={t("meetAssistInquiry.Total.ServiceFee")}
          amount={<AnimatedYen amount={price.total} />}
          className="tw:text-[14px] tw:leading-[20px] tw:text-[#282828]"
        />
        {/* Per-guest breakdown and night note fold in only when they apply. */}
        <Fold isOpen={price.hasGuestBreakdown || price.isNightSurcharge}>
          <div className="tw:flex tw:flex-col tw:gap-y-[14px]">
            <Divider className="tw:!bg-[#E8E8E8]" />
            <div className="tw:px-[10px] tw:flex tw:flex-col tw:gap-y-2 tw:text-[13px] tw:leading-[18.5px] tw:text-[#7C7B7B]">
              {price.hasGuestBreakdown && (
                <PriceLine label={t("meetAssistInquiry.Total.FirstGuest")} amount={<AnimatedYen amount={price.firstGuestPrice} />} />
              )}
              <Fold isOpen={price.hasGuestBreakdown && price.additionalGuestCount > 0}>
                <PriceLine
                  label={<SwapText value={t("meetAssistInquiry.Total.AdditionalGuests", { count: price.additionalGuestCount })} />}
                  amount={<AnimatedYen amount={price.additionalGuestsPrice} />}
                />
              </Fold>
              <Fold isOpen={price.isNightSurcharge}>
                <span>{t("meetAssistInquiry.Total.NightSurcharge", { rate: price.nightSurchargeRate })}</span>
              </Fold>
            </div>
          </div>
        </Fold>
      </div>

      <div className="tw:px-5 tw:py-5 tw:flex tw:items-center tw:justify-between tw:text-[#282828]">
        <span>
          <span className="tw:font-medium tw:text-[16px] tw:leading-[20px]">{t("meetAssistInquiry.Total.Total")}</span>{" "}
          <span className="tw:text-[12px] tw:leading-[17px] tw:text-[#8D8D8D]">{t("meetAssistInquiry.Total.TaxIncluded")}</span>
        </span>
        <AnimatedYen amount={price.total} className="tw:font-bold tw:text-[16px]" />
      </div>
    </div>
  );
};

/** Grey bar linking to the cancellation policy page (opens in a new tab). */
export const CancellationPolicyLink = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  return (
    <Link
      to={`/${language}/cancellation-policy`}
      target="_blank"
      rel="noopener noreferrer"
      className="tw:flex tw:items-center tw:justify-center tw:gap-x-2 tw:px-3 tw:py-2 tw:bg-[#F1F1F1] tw:hover:bg-[#E8E8E8] tw:!rounded-[3px] tw:!no-underline tw:transition-colors tw:duration-200"
    >
      <InfoIcon size={14} color="#8D8D8D" aria-hidden="true" />
      <span className="tw:font-hanken-grotesk tw:text-[14px] tw:leading-[20px] tw:!text-[#4E4E4E]">
        {t("meetAssistInquiry.CancellationPolicy")}
      </span>
    </Link>
  );
};

/**
 * Gold submit button with the request's error (if any) folding in above it.
 * Disabled while sending so a double click cannot send two emails; a small
 * spinner shows the request is on its way.
 */
export const SubmitPanel = ({ isSubmitting, submitErrorKey }) => {
  const { t } = useTranslation();
  const pressMotion = usePressMotion(1.01, 0.98);
  return (
    <div className="tw:flex tw:flex-col">
      <Fold isOpen={Boolean(submitErrorKey)}>
        <div className="tw:pb-3">
          <p
            role="alert"
            className="tw:!mb-0 tw:px-4 tw:py-3 tw:!rounded-[5px] tw:bg-[#FDECEA] tw:font-hanken-grotesk tw:text-[13px] tw:leading-[18.5px] tw:!text-[#C5221F]"
          >
            {submitErrorKey && t(`meetAssistInquiry.${submitErrorKey}`)}
          </p>
        </div>
      </Fold>
      <motion.button
        {...(isSubmitting ? {} : pressMotion)}
        type="submit"
        disabled={isSubmitting}
        aria-busy={isSubmitting}
        className={cn(
          "tw:h-[50px] tw:w-full tw:px-8 tw:!rounded-[5px] tw:bg-[#DBBE78] tw:flex tw:items-center tw:justify-center tw:gap-x-3 tw:font-hanken-grotesk tw:font-bold tw:!text-[15px] tw:!leading-[22px] tw:text-white tw:transition-colors tw:duration-200",
          isSubmitting ? "tw:opacity-80 tw:cursor-wait" : "tw:hover:bg-[#C9AA5F]"
        )}
      >
        <AnimatePresence initial={false}>
          {isSubmitting && (
            <motion.span
              aria-hidden="true"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 16 }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: DURATION.fast, ease: EASE_OUT }}
              className="tw:size-4 tw:!rounded-full tw:border-2 tw:border-solid tw:border-white/40 tw:border-t-white tw:animate-spin"
            />
          )}
        </AnimatePresence>
        <SwapText value={isSubmitting ? t("meetAssistInquiry.Submitting") : t("meetAssistInquiry.Submit")} />
      </motion.button>
    </div>
  );
};
